import { useEffect, useState } from 'react';
import {
  Button,
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  FormSection,
  Input,
  StatusBadge,
} from '@/components/ui';
import { UserSearchSelect } from '@/features/users/components/UserSearchSelect';
import type { AdjustDirection } from './ManualAdjustDialog';
import type { BulkAdjustResult } from '../types';

export interface BulkAdjustModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  direction: AdjustDirection;
  busy?: boolean;
  /** 직전 실행 결과 (BulkAdjustResult). 닫을 때까지 유지해 성공/실패 집계를 표시. */
  result?: BulkAdjustResult | null;
  onSubmit: (input: {
    userIds: ReadonlyArray<string>;
    amount: number;
    reason: string;
    memo?: string;
  }) => void;
}

const TITLE: Record<AdjustDirection, string> = {
  grant: '일괄 지급',
  deduct: '일괄 차감',
};

export const BulkAdjustModal = ({
  open,
  onOpenChange,
  direction,
  busy,
  result,
  onSubmit,
}: BulkAdjustModalProps) => {
  const [userIds, setUserIds] = useState<ReadonlyArray<string>>([]);
  const [amount, setAmount] = useState('');
  const [reason, setReason] = useState('');
  const [memo, setMemo] = useState('');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) {
      setUserIds([]);
      setAmount('');
      setReason('');
      setMemo('');
      setError(null);
    }
  }, [open]);

  const handleSubmit = () => {
    setError(null);
    if (userIds.length === 0) {
      setError('회원을 1명 이상 선택하세요.');
      return;
    }
    const parsed = Number(amount);
    if (!Number.isFinite(parsed) || parsed <= 0) {
      setError('금액은 1 이상의 정수여야 합니다.');
      return;
    }
    if (reason.trim() === '') {
      setError('사유를 입력하세요.');
      return;
    }
    onSubmit({
      userIds,
      amount: Math.floor(parsed),
      reason: reason.trim(),
      ...(memo.trim() ? { memo: memo.trim() } : {}),
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{TITLE[direction]}</DialogTitle>
          <DialogDescription>
            회원을 다중 선택해 동일 금액/사유로 {direction === 'grant' ? '지급' : '차감'}합니다.
            회원별 독립 처리되어 일부 실패해도 나머지는 적용됩니다.
          </DialogDescription>
        </DialogHeader>

        {error ? (
          <div
            className="rounded-md p-2.5 text-[12px]"
            role="alert"
            style={{ background: 'var(--status-danger-bg)', color: 'var(--status-danger-fg)' }}
          >
            {error}
          </div>
        ) : null}

        <div className="flex flex-col gap-3">
          <FormSection density="compact" label="대상 회원 (다중 선택)" required>
            <UserSearchSelect
              multi
              value={userIds}
              onChange={setUserIds}
              testId="bulk-adjust-users"
              ariaLabel="대상 회원 다중 선택"
              emptyHint="이름·이메일·전화번호로 검색해 회원을 추가하세요."
            />
          </FormSection>
          <FormSection density="compact" label="금액" required>
            <Input
              type="number"
              min={1}
              step={1}
              inputMode="numeric"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              aria-label="금액"
            />
          </FormSection>
          <FormSection density="compact" label="사유" required>
            <Input
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              maxLength={200}
              required
              aria-label="사유"
            />
          </FormSection>
          <FormSection density="compact" label="메모 (선택)">
            <Input
              value={memo}
              onChange={(e) => setMemo(e.target.value)}
              maxLength={500}
              aria-label="메모"
            />
          </FormSection>
        </div>

        {result ? (
          <div
            className="rounded-md p-3 text-[12px]"
            style={{ background: 'var(--surface-1)', border: '1px solid var(--divider-strong)' }}
            data-testid="bulk-adjust-result"
          >
            <div className="mb-2 flex items-center gap-2">
              <StatusBadge
                variant={result.failed === 0 ? 'success' : 'warning'}
                label={`성공 ${result.succeeded} / 실패 ${result.failed} (총 ${result.total})`}
              />
            </div>
            {result.failures.length > 0 ? (
              <ul className="flex flex-col gap-1">
                {result.failures.map((f) => (
                  <li key={f.userId} style={{ color: 'var(--content-muted)' }}>
                    실패: {f.reason}
                  </li>
                ))}
              </ul>
            ) : null}
          </div>
        ) : null}

        <DialogFooter>
          <DialogClose asChild>
            <Button variant="ghost" size="sm">
              닫기
            </Button>
          </DialogClose>
          <Button
            variant={direction === 'deduct' ? 'danger' : 'primary'}
            size="sm"
            disabled={busy}
            onClick={handleSubmit}
            data-testid="bulk-adjust-submit"
          >
            {TITLE[direction]}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
